import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft, Globe, Phone, Mail, Building2, MapPin, Tag, Loader2 } from "lucide-react";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import { companiesService } from "../../../services/companiesService";
import type { Company } from "../../../types";
import { CompanyAvatar } from "../../../components/CompanyAvatar";

export function ExhibitorCompanyPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const eventId = user?.assignedEventId ?? "";
  const companyId = user?.assignedCompanyId ?? "";

  const [company, setCompany] = useState<Company | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId || !companyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const data = await companiesService.getById(companyId, eventId, "admin");
        if (!cancelled) setCompany(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, companyId]);

  if (loading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }
  if (!company) {
    return <div className="p-6 text-gray-500 text-[14px]">Компания не найдена</div>;
  }

  const fields: Array<{ label: string; value: string; icon: React.ReactNode }> = [
    { label: "Название компании", value: company.name, icon: <Building2 size={13} /> },
    { label: "Категория", value: company.category, icon: <Tag size={13} /> },
    { label: "Страна", value: company.country ?? "", icon: <MapPin size={13} /> },
    { label: "Город", value: company.city ?? "", icon: <MapPin size={13} /> },
    { label: "Сайт", value: company.website ?? "", icon: <Globe size={13} /> },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/exhibitor")}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hidden sm:block"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-[19px] text-gray-900">Профиль компании</h1>
          <p className="text-[12px] text-gray-400">
            Справочная информация о компании. Правки в раздел вносит организатор.
          </p>
        </div>
      </div>

      <div className="rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-2.5 text-[12px] text-slate-600">
        Кабинет экспонента — аналитика и скачивание QR. Редактирование профиля недоступно.
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <h2 className="text-[13px] text-gray-700 mb-3">Логотип</h2>
        <div className="flex items-center gap-4">
          <CompanyAvatar
            name={company.name}
            logoUrl={company.logoUrl}
            size="xl"
            className="rounded-2xl"
          />
          <p className="text-[12px] text-gray-400">Отображается посетителям при сканировании</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <h2 className="text-[13px] text-gray-700">Основная информация</h2>
        {fields.map((f) => (
          <div key={f.label}>
            <div className="text-[11px] text-gray-400 mb-1 flex items-center gap-1">
              {f.icon} {f.label}
            </div>
            <div className="px-3 py-2 rounded-lg bg-gray-50 text-[13px] text-gray-800 min-h-[38px]">
              {f.value || "—"}
            </div>
          </div>
        ))}
        <div>
          <div className="text-[11px] text-gray-400 mb-1">Описание для посетителей</div>
          <div className="px-3 py-2 rounded-lg bg-gray-50 text-[13px] text-gray-700 leading-relaxed whitespace-pre-wrap min-h-[80px]">
            {company.shortAbout || "—"}
          </div>
        </div>
        {(company.tags?.length ?? 0) > 0 && (
          <div>
            <div className="text-[11px] text-gray-400 mb-1.5">Теги</div>
            <div className="flex flex-wrap gap-1.5">
              {company.tags!.map((tag) => (
                <span
                  key={tag}
                  className="text-[12px] bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <div>
          <h2 className="text-[13px] text-gray-700">Общие контакты стенда</h2>
          <p className="text-[11px] text-gray-400 mt-0.5">
            Открываются при сканировании QR флажка стенда
          </p>
        </div>
        {[
          {
            label: "Общий телефон",
            value: company.contact.phone ?? "",
            icon: <Phone size={13} />,
          },
          {
            label: "Общий email",
            value: company.contact.email ?? "",
            icon: <Mail size={13} />,
          },
        ].map((f) => (
          <div key={f.label}>
            <div className="text-[11px] text-gray-400 mb-1 flex items-center gap-1">
              {f.icon} {f.label}
            </div>
            <div className="px-3 py-2 rounded-lg bg-gray-50 text-[13px] text-gray-800 min-h-[38px]">
              {f.value || "—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
